// ── capstone-columns: the capstone pipeline-stage table columns (Wave 8) ────
// This table renders the capstone PROCESSING PIPELINE (intake → conversion →
// evaluation → certificate-trigger) as a contract reference — NOT fabricated
// submission data (there is no capstone read route). Each stage carries its
// honest posture badge so the dark agent-eval lane and the not-configured
// certificate trigger read as exactly what they are, never as a fake status.

import { Badge, StatusDot, humanizeLabel } from "@vjal/ui";
import type { DataTableColumn } from "@vjal/ui";
import type { CapstonePipelineStage } from "./capstone-helpers";

export type CapstoneStageRow = CapstonePipelineStage & Record<string, unknown>;

function postureTone(posture: string): "success" | "info" | "warning" | "danger" {
  if (posture === "not-configured") return "danger";
  if (posture === "unavailable") return "warning";
  if (posture === "gated") return "info";
  return "info";
}

export function buildCapstoneColumns(): DataTableColumn<CapstoneStageRow>[] {
  return [
    {
      key: "label",
      header: "Stage",
      render: (row) => (
        <span className="inline-block min-w-[8rem] whitespace-nowrap font-medium text-strong">{row.label}</span>
      )
    },
    {
      key: "description",
      // Gemini residual: "What happens" hard-clips mid-sentence at the card edge
      // without a usable scroll affordance — reserve width + title for full text.
      header: <span className="whitespace-nowrap">What happens</span>,
      render: (row) => (
        <span className="block min-w-[18rem] max-w-[28rem] truncate text-sm text-ink-muted" title={row.description}>
          {row.description}
        </span>
      )
    },
    {
      key: "posture",
      header: "State",
      render: (row) => (
        <span className="inline-flex min-w-[7rem] whitespace-nowrap">
          <Badge variant={postureTone(row.posture)}>{humanizeLabel(row.posture)}</Badge>
        </span>
      )
    },
    {
      key: "id",
      header: "Lane",
      render: (row) => (
        <span className="inline-flex min-w-[6rem] whitespace-nowrap">
          <StatusDot tone={row.posture === "not-configured" ? "err" : "info"} label={humanizeLabel(row.id)} />
        </span>
      )
    }
  ];
}
